import { type LatLngBounds } from '~/_shared/types/latLng';
import { notNullsy } from '~/_shared/utils/typeGuards';
import { createUuid } from '../../_shared/utils/createUuid';
import {
  createBoundaryTerritoryFromResponse,
  createBoundaryTerritorySettingsRequestFromBoundaryTerritorySettings,
  createRequestFromBoundaryTerritoryGroupSettings,
  getBoundaryTerritoryGroupMatchupRequest, sortBoundaryTerritoriesFromResponse,
} from '../boundaryTerritoryGroups/boundaryTerritoryGroups.factory';
import {
  type BoundaryTerritoryGroup,
  type BoundaryTerritoryGroupModel,
} from '../boundaryTerritoryGroups/boundaryTerritoryGroups.state';
import {
  type BoundaryTerritoriesResponse,
  type BoundaryTerritoryGroupDetailsExtraDataRequest,
  type BoundaryTerritoryGroupDetailsFilterRequest,
  type BoundaryTerritoryGroupDetailsItemResponse,
  type BoundaryTerritoryGroupDetailsRequestQuery,
  type BoundaryTerritoryGroupDetailsResponse,
} from './boundaryTerritoryDetails.repository';
import {
  type BoundaryTerritories,
  type BoundaryTerritoryGroupDetails, type BoundaryTerritoryGroupDetailsItem,
} from './boundaryTerritoryDetails.types';

export const createBoundaryTerritoryGroupDetailsFromResponse = (
  response: BoundaryTerritoryGroupDetailsResponse,
  extraDataMapping?: { [data_query_id: string]: BoundaryTerritoryGroupDetailsExtraDataRequest[number] }
): BoundaryTerritoryGroupDetails => {
  return {
    boundaryDetails: createBoundaryTerritoryGroupDetailsItemFromResponse(response.boundary_details, extraDataMapping),
    boundaryGroupId: response.boundary_group_id,
    boundaryTerritoryGroupId: response.boundary_territory_group_id,
    boundaryTerritoryGroupQueryId: response.boundary_territory_group_query_id,
    mapId: response.map_id,
  };
};

export const createBoundaryTerritoriesFromResponse = (
  response: BoundaryTerritoriesResponse,
): BoundaryTerritories => {
  return {
    boundaryGroupId: response.boundary_group_id,
    boundaryTerritoryGroupId: response.boundary_territory_group_id,
    boundaryTerritories: response.boundary_territories
      .map(territory => createBoundaryTerritoryFromResponse(territory))
      .sort(sortBoundaryTerritoriesFromResponse),
    boundaryTerritoryGroupQueryId: response.boundary_territory_group_query_id,
    mapId: response.map_id,
  };
};

type ExtraDataMapping = { readonly [data_query_id: string]: BoundaryTerritoryGroupDetailsExtraDataRequest[number] };

export type BoundingBoxRequest = {
  sw: {
    latitude: number;
    longitude: number;
  };
  ne: {
    latitude: number;
    longitude: number;
  };
};

const convertLatLngBoundsToDetailsRequestBox = (boundingBox: LatLngBounds): BoundingBoxRequest => {
  return {
    sw: {
      latitude: boundingBox.sw.lat,
      longitude: boundingBox.sw.lng,
    },
    ne: {
      latitude: boundingBox.ne.lat,
      longitude: boundingBox.ne.lng,
    },
  };
};

export const createBoundaryTerritoryGroupDetailsRequestQuery = (
  territoryGroup: BoundaryTerritoryGroupModel,
  boundaryTerritoryGroupQueryId: string,
  filter?: BoundaryTerritoryGroupDetailsFilterRequest,
  extraData?: BoundaryTerritoryGroupDetailsExtraDataRequest,
  boundingBox?: LatLngBounds | null,
): { readonly request: BoundaryTerritoryGroupDetailsRequestQuery; readonly extraDataMapping?: ExtraDataMapping } => {
  let extraDataMapping: { [data_query_id: string]: BoundaryTerritoryGroupDetailsExtraDataRequest[number] } | undefined;
  let dtoExtraData: BoundaryTerritoryGroupDetailsRequestQuery['extra_data'] | undefined;

  if (extraData && extraData.length) {
    extraDataMapping = {};
    dtoExtraData = extraData.map(item => {
      const data_query_id = createUuid();
      if (extraDataMapping) {
        extraDataMapping[data_query_id] = item;
      }
      if (item.type === 'Demographic') {
        return {
          data_query_id,
          type: 'Demographic' as const,
          demographic_id: item.demographicId,
        };
      }
      return {
        data_query_id,
        type: 'SpreadsheetColumn' as const,
        function: item.function,
        column: {
          spreadsheet_id: item.column.spreadsheetId,
          column_id: item.column.columnId,
        },
      };
    });
  }

  return {
    request: {
      boundary_group_id: territoryGroup.boundaryGroupId,
      boundary_territory_group_id: territoryGroup.boundaryTerritoryGroupId,
      boundary_territory_group_query_id: boundaryTerritoryGroupQueryId,
      map_id: territoryGroup.mapId,
      matchings: getBoundaryTerritoryGroupMatchupRequest(territoryGroup.matchings),
      settings: createBoundaryTerritorySettingsRequestFromBoundaryTerritorySettings(territoryGroup.settings),
      filter,
      bounding_box: boundingBox ? convertLatLngBoundsToDetailsRequestBox(boundingBox) : undefined,
      extra_data: dtoExtraData,
    },
    extraDataMapping,
  };
};

export const createBoundaryTerritoryRequestFromBoundaryTerritoryGroup = (
  boundaryTerritoryGroup: BoundaryTerritoryGroup,
  queryId: string,
  request?: Partial<BoundaryTerritoryGroupDetailsRequestQuery>,
): BoundaryTerritoryGroupDetailsRequestQuery => {
  return {
    boundary_territory_group_query_id: queryId,
    map_id: boundaryTerritoryGroup.mapId,
    boundary_group_id: boundaryTerritoryGroup.boundaryGroupId,
    boundary_territory_group_id: boundaryTerritoryGroup.boundaryTerritoryGroupId,
    matchings: getBoundaryTerritoryGroupMatchupRequest(boundaryTerritoryGroup.matchings),
    settings: createRequestFromBoundaryTerritoryGroupSettings(boundaryTerritoryGroup.settings),
    ...request,
  };
};

const createBoundaryTerritoryGroupDetailsItemFromResponse = (
  response: BoundaryTerritoryGroupDetailsItemResponse,
  extraDataMapping?: { [data_query_id: string]: BoundaryTerritoryGroupDetailsExtraDataRequest[number] }
): BoundaryTerritoryGroupDetailsItem => {
  const extraData = createDetailsExtraData(response, extraDataMapping);

  return {
    assignments: response.assignments,
    manualAssignments: response.manual_assignments,
    counts: response.counts,
    extraData,
    numericalData: {
      totals: response.numerical_data.totals,
      totalHighest: response.numerical_data.total_highest,
      totalLowest: response.numerical_data.total_lowest,
    },
    hasEmpty: response.has_empty,
  };
};

const createDetailsExtraData = (
  response: BoundaryTerritoryGroupDetailsItemResponse,
  extraDataMapping?: { [data_query_id: string]: BoundaryTerritoryGroupDetailsExtraDataRequest[number] }
) => {
  let extraData: BoundaryTerritoryGroupDetailsItem['extraData'];
  if (response.extra_data && extraDataMapping) {
    const items = Object.entries(response.extra_data).map(([data_query_id, responseData]) => {
      const original = extraDataMapping[data_query_id];

      if (!original) {
        return undefined;
      }

      const data = Object.fromEntries(Object.entries(responseData).map(([boundaryId, values]) => {
        return [Number(boundaryId), {
          value: values.value ?? null,
          formattedValue: values.formatted_value ?? values.value?.toString() ?? null,
        }];
      }));

      return {
        ...original,
        data,
      };
    }).filter(notNullsy);

    if (items.length) {
      extraData = items;
    }
  }

  return extraData;
};
